import React, { Component, Suspense, lazy } from "react";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import API from "./apiCalls/accountAPI";
import jobTransitionAPI from "./apiCalls/jobTransitionCalls";
import { getCookie, isEngineIframeLoad, isEngineOrIframeMode, logout } from "./util/index";
import asyncComponent from "./HOC/AsyncComponent";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import store from './store';
import { setCurrentBrokerJob } from './actions/searchContextActions';
import JobQueueHeader from "./components/JobQueue/JobQueueHeader";
import { AppContext, AppProvider } from "./appProvider";
//import { LogLevel } from '@grafana/faro-web-sdk';
import { getBrokerUiEnvironment } from './util/index';
import { getQueryVariable } from "./apiCalls/jobInfo";
import { prefetchShipmentDocuments } from "./apiCalls/MainFormRequests";
import WithChatService from "./ChatApp/ChatService";
import { showChatApp } from "./ChatApp/helper";
import { SocketClient, CONNECTION_STATES, SYSTEM_EVENTS } from "./socket/socket-client";
import { 
  initHandlers,
  startEntryStatusListening,
  stopEntryStatusListening,
  startBrokerShipmentListening,
  stopBrokerShipmentListening
} from "./socket/entry-socket";
import { getWebSocketUrl, shouldUseSocketForEngineShipment as shouldUseSocketForEngineShipmentUtil, isSocketEnabled } from "./socket/entry-socket/utils";
import { isEU } from "./apiCalls/jobTransitionCalls";
import { ToastProvider, toast } from "./commonComponents/Toast";

// Create socket client instance with project-specific configuration
const socketClient = new SocketClient({
  webSocketUrl: getWebSocketUrl(),
  authToken: getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN) || '',
  // enableInactivityDisconnect: true,  // Default: false - Set to true to enable auto-disconnect when tab is hidden
  // inactivityTimeout: 5 * 60 * 1000,  // Default: 5 minutes - Custom timeout in milliseconds (only used if enableInactivityDisconnect is true)
});

// Initialize socket handlers with the socket client instance
initHandlers(socketClient);


const LoginFallback = () => (
  <div className="async-login-fallback" aria-hidden="true" />
);
const JobQueueFallback = () => (
  <div className="async-jobqueue-fallback" aria-hidden="true" />
);

const loadLogin = () => import("./components/Login/Login.js");
const loadJobQueue = () => import("./containers/JobQueueContainer");
const currentPath = typeof window !== "undefined" ? window.location.pathname : "";

const ENGINE_IFRAME_PAGES = [
  "broker-panel",
  "custom-statement",
  "invoices-us",
  "invoices-ca",
  "parts-library",
  "parts",
];

const getEngineIframeTargetPath = () => {
  const page = getQueryVariable("page");
  const target = page && ENGINE_IFRAME_PAGES.includes(page) ? page : "broker-panel";
  const search = typeof window !== "undefined" ? window.location.search : "";
  return `/${target}${search}`;
};

const engineIframeLoad = typeof window !== "undefined" && isEngineIframeLoad();
const AsyncLogin = asyncComponent(loadLogin, LoginFallback, {
  prefetch: !engineIframeLoad && (currentPath === "/" || currentPath === ""),
});
const AsyncJobQueue = asyncComponent(loadJobQueue, JobQueueFallback, {
  prefetch: engineIframeLoad || currentPath === "/broker-panel",
});
const AsyncForgot = asyncComponent(() => {
  return import("./components/Login/Forgot.js");
});
const AsyncReset = asyncComponent(() => {
  return import("./components/Login/Reset.js");
});
const PreDailyPageLazy = lazy(() => {
  return import("./components/AccountMenu/components/payment/cbp-service/PreDailyPage");
});
const InvoiceUs = lazy(() => import("./components/AccountMenu/components/payment/Invoice/index"));
const InvoiceCanada = lazy(() => import("./components/AccountMenu/components/payment/InvoiceCanada/index"));
const InvoiceWrapper = lazy(() => import("./commonComponents/invoiceWrapper"));
const ManageProductHistory = lazy(() => import("./components/AccountMenu/components/productLineHistory/History"));
const Parts = lazy(() => import("./components/AccountMenu/components/productLineHistory/Parts"));
const ReconApp = lazy(() => import("./Recon_App/index"));
const ConversationsContainer = lazy(() => import("./ChatApp/Conversations/ConversationsContainer"));
const XmlRendererWithRouter = lazy(() =>
  import("./xmlRenderer").then((m) => ({ default: m.XmlRendererWithRouter }))
);
const XmlReviewerWithRouter = lazy(() =>
  import("./xmlReviewer").then((m) => ({ default: m.XmlReviewerWithRouter }))
);
const XmlSentWithRouter = lazy(() =>
  import("./xmlSent").then((m) => ({ default: m.XmlSentWithRouter }))
);
const XmlRequestWithRouter = lazy(() =>
  import("./xmlRequest").then((m) => ({ default: m.XmlRequestWithRouter }))
);
const RouteFallback = () => <div className="async-route-fallback" aria-hidden="true" />;



const optionsState = {
  AL: "AL", AK: "AK", AZ: "AZ", AR: "AR", CA: "CA", CO: "CO", CT: "CT", DE: "DE", FL: "FL", GA: "GA",
  HI: "HI", ID: "ID", IL: "IL", IN: "IN", IA: "IA", KS: "KS", KY: "KY", LA: "LA", ME: "ME", MD: "MD",
  MA: "MA", MI: "MI", MN: "MN", MS: "MS", MO: "MO", MT: "MT", NE: "NE", NV: "NV", NH: "NH", NJ: "NJ",
  NM: "NM", NY: "NY", NC: "NC", ND: "ND", OH: "OH", OK: "OK", OR: "OR", PA: "PA", RI: "RI", SC: "SC",
  SD: "SD", TN: "TN", TX: "TX", UT: "UT", VT: "VT", VA: "VA", WA: "WA", WV: "WV", WI: "WI", WY: "WY",
}

const optionsCountry = {
  USA: "United States",
  KH: "Canada"
}
const euCountries = ['ES', 'NL'];
class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      kxUser: "",
      low: 0,
      high: 0,
      direction: "DESC",
      count: 0,
      shipId: "NoJobsAvailable",
      bolNumber: "",
      bolDocNumber: "",
      clickedShipId: "",
      kxToken: getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN),
      toggleInProcess: false,
      toggleCompleted: false,
      filterContainerOpen: true,
      formValidatedForSV: false,
      aceOpen: false,
      selectedOpsForm: "",
      redirect: "/",
      selectedForm: "DOC_ISF",
      clickedShipIdHasBeenChanged: false,
      myAccountOpen: false,
      modalOpen: "",
      sessionData: {
        email: "",
        fullName: "",
        phone: ""
      },
      filterModalOpen: false,
      newerDisabled: true,
      olderDisabled: false,
      loadingJobs: true,
      shipmentCountry: ""
    };
    App.routeUser = App.routeUser.bind(this);
    this.setCurrentJobData = this.setCurrentJobData.bind(this)
  }

  setShipmentCountry = (countryCode, countryOfBusiness) => {
    this.setState({
      shipmentCountry: countryCode,
      countryOfBusiness: countryOfBusiness
    })
  }

  validateUserInformation = (userData, paymentInfo) => {

    let validInfo = [true, true];
    try {
      if (userData.userInfo.bondInfo.bondNumber.length <= 0) validInfo[0] = false;
      if (userData.userInfo.bondInfo.bondAmount.length <= 0) validInfo[0] = false;
      if (userData.userInfo.bondInfo.bondType.length <= 0) validInfo[0] = false;
      if (userData.userInfo.bondInfo.bondOwner.length <= 0) validInfo[0] = false;
    } catch {
      validInfo[0] = false;
    }
    try {
      if (paymentInfo[0].paymentMethod.length <= 0) validInfo[1] = false;
      if (paymentInfo[0].fullName.length <= 0) validInfo[1] = false;
      if (paymentInfo[0].paymentInstrumentGuid.length <= 0) validInfo[1] = false;
      if (paymentInfo[0].lastFourDigit.length <= 0) validInfo[1] = false;
      if (paymentInfo[0].expiryDate.length <= 0) validInfo[1] = false;
      if (paymentInfo[0].type.length <= 0) validInfo[1] = false;
    } catch {
      validInfo[1] = false;
    }
    return validInfo;
  };

  getImporterList = (filerCode, kxUserData) => {
    if(isEngineOrIframeMode() || getQueryVariable('isShipment') == 'true'){
      kxUserData.importerList = [];
      this.setState({
          kxUser: kxUserData
        });
        return;
    }
    API.getImporterList(filerCode).then((res) => {
      let importerList = res.data.eventMessage.userGuidFilerCodeMap;
      importerList.forEach((item) => {
        item.value = (item.value).toUpperCase()
        item.text = item.value;
      })
      setTimeout(() => {
        kxUserData.importerList = importerList;
        this.setState({
          kxUser: kxUserData
        });
        sessionStorage.setItem("kxUser", JSON.stringify(kxUserData))
      }, 0)
    }).catch(() => {
      this.setState({
        kxUser: kxUserData
      });
    })
  }
  setUserData = () => {
    API.getUserInfo(getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN))
      .then((responses) => {
        return Promise.all(responses.map(function (res) {
          return res.json();
        }));
      })
      .then((responseData) => {
        const kxUserError = responseData.find(xdata=>xdata.eventType=="GET_KXUSER");
        if(kxUserError?.eventMessage?.code >= 400){
          console.error("KX User Error:", kxUserError);
          toast.warning("Something Went Wrong !")
        }
        if (responseData[0].msg == "Failed") {
          if (window.confirm("It looks like you were logged out. Redirecting to login page...")) {
            logout();
          }
        }

        let kxUserData = responseData[0].eventMessage;
        kxUserData.teamMembers = responseData[1].eventMessage.teamMembers;
        kxUserData.channelConnections = responseData[2].eventMessage.channelConnections;
        window.typeOfUser = kxUserData.userBasicInfo.typeOfUser;
        let filerCode = get(kxUserData, "userInfo.brokerAdditionalInfo.accountSecurityNumber") || get(kxUserData, "userInfo.brokerAdditionalInfo.filerCode");
        this.getImporterList(filerCode, kxUserData);
        let countryCode = get(kxUserData, "userInfo.brokerAdditionalInfo.countryCode", "");
        if (euCountries.includes(countryCode) && !localStorage.getItem("expandIsfFormWidthb")) {
          localStorage.setItem("expandIsfFormWidthb", "true")
        }
      })
  };

  getPaymentData = async () => {
    try {
      const response = await API.getPaymentData(getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN));
      let userPaymentData = response.eventMessage.paymentInstruments;
      this.setState({ paymentData: userPaymentData });
    } catch (err) {
      console.error("ERROR getting payment data:", err);
    }
  };

  getCompanyInfo = () => {
    let info = this.convertArrayToMap(get(this.state, "kxUser.userInfo.companyInformation.businessAddress", ""));
    let businessName = get(this.state, "kxUser.userInfo.companyInformation.businessName", "");
    let address = info["STREET_NUMBER"];
    let address2 = info["STREET_NAME"];
    let city = info["CITY"];
    let state = info["COUNTRY_SUB_ENTITY"] !== "" ? info["COUNTRY_SUB_ENTITY"] : optionsState["AL"];
    let zip = info["POSTAL_CODE"];
    let country = info["COUNTRY"] !== "" ? info["COUNTRY"] : optionsCountry["USA"];
    let entryMin = get(this.state, "kxUser.entryNumberDetails.startRange", null);
    let entryMax = get(this.state, "kxUser.entryNumberDetails.endRange", null);

    API.companyInfo(getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN), "", businessName, address, address2, city, state, zip, country, entryMin, entryMax)
      .then((res) => {
        return res.json();
      })
      .then((response) => {
        if (response.msg === "Event not allowed without valid token" || response.msg === "Expired Token" || response.code === 401) {
          console.log('"Event not allowed without valid token": ', "Event not allowed without valid token");
          logout();
        }
      })
      .catch((err) => console.error("companyInfo", err));
  };

  // Converts shipping destinations array into a map
  convertArrayToMap = (arr) => {
    let temp = [];
    for (let i = 0; i < arr.length; i++) temp[arr[i].type] = arr[i].information ? arr[i].information : "";
    return temp;
  };

  componentWillMount() {
    this.loadUserSessionData();
  }

  toggleModal = (name) => {
    this.setState({ modalOpen: name });
  };

  setCurrentJobData = async (shipmentId) => {
    if (!(shipmentId || window.shipmentId)) {
      return;
    }
    
    const shipmentIdToUse = shipmentId || window.shipmentId;
    
    try {
      // Fallback to HTTP if socket is not connected
      const res = await jobTransitionAPI.searchCurrentJob(shipmentIdToUse);
      
      for(let onejob of res?.data?.eventMessage?.brokerJobs?.currentBrokerJobs ||[]){
        onejob.countryCode = onejob.countryCode || getQueryVariable("countryHttpParameter");
      }
      let currentJobState = get(res.data.eventMessage, "brokerJobs.currentBrokerJobs");
      if(currentJobState && currentJobState.length){
        currentJobState[0].timeRemaining = null;
      }
      if(isEqual(window[`GLOBAL_LastJobData${shipmentIdToUse}`],currentJobState)){
        return;
      }
      window[`GLOBAL_LastJobData${shipmentIdToUse}`] = currentJobState;
      store.dispatch(setCurrentBrokerJob(shipmentIdToUse, currentJobState))
    } catch (err) {
      console.error('[App] Error fetching current job data:', err);
    }
  }

  // Preserves critical fields from existing job data if missing in socket response
  preserveJobFields = (newJob, existingJob) => {
    if (!existingJob) return;
    
    // Preserve critical fields that Card component needs
    newJob.customer = newJob.customer || existingJob.customer;
    newJob.entryNumber = newJob.entryNumber || existingJob.entryNumber;
    newJob.purchaseOrder = newJob.purchaseOrder || existingJob.purchaseOrder;
    newJob.eta = newJob.eta || existingJob.eta;
    newJob.etd = newJob.etd || existingJob.etd;
    newJob.podEta = newJob.podEta || existingJob.podEta;
    newJob.bolType = newJob.bolType || existingJob.bolType;
    newJob.brokerInfo = newJob.brokerInfo || existingJob.brokerInfo;
    newJob.serviceType = newJob.serviceType || existingJob.serviceType;
    newJob.countryCode = newJob.countryCode || existingJob.countryCode || getQueryVariable("countryHttpParameter");
    newJob.formType = newJob.formType || existingJob.formType || "";
    newJob.countryOfBusiness = newJob.countryOfBusiness || existingJob.countryOfBusiness || "";
  }

  // Clears all debounce timers
  clearAllDebounceTimers = () => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.brokerShipmentDebounceTimer) {
      clearTimeout(this.brokerShipmentDebounceTimer);
      this.brokerShipmentDebounceTimer = null;
    }
  }

  registerHeaderMessagesRefresh = (refreshFn) => {
    this.refreshHeaderMessages =
      typeof refreshFn === 'function' ? refreshFn : null;
  };

  // Shared method to process broker job updates from socket
  processBrokerJobUpdate = (data) => {
    const shipmentIdToUse = this.state.clickedShipId || window.shipmentId;
    if (!shipmentIdToUse) return;

    if (!data || typeof data !== 'object') return;

    const brokerJobs = get(data, "brokerJobs.currentBrokerJobs", null);
    if (!brokerJobs || !Array.isArray(brokerJobs) || brokerJobs.length === 0) return;

    // Preserve existing data to maintain required fields (countryCode, formType, etc.)
    const existingBrokerJobs = window[`GLOBAL_LastJobData${shipmentIdToUse}`] || [];
    const existingJob = Array.isArray(existingBrokerJobs) && existingBrokerJobs[0] ? existingBrokerJobs[0] : null;

    for(let onejob of brokerJobs || []){
      if (onejob) {
        // Preserve critical fields from existing job if missing in update
        if (existingJob) {
          this.preserveJobFields(onejob, existingJob);
        } else {
          onejob.countryCode = onejob.countryCode || getQueryVariable("countryHttpParameter");
        }
      }
    }
    
    if(brokerJobs && brokerJobs.length){
      brokerJobs[0].timeRemaining = null;
    }
    
    if(!isEqual(window[`GLOBAL_LastJobData${shipmentIdToUse}`], brokerJobs)){
      window[`GLOBAL_LastJobData${shipmentIdToUse}`] = brokerJobs;
      store.dispatch(setCurrentBrokerJob(shipmentIdToUse, brokerJobs));
    }
  }

  // Handles entry status updates from polling
  handleEntryStatusUpdate = (data) => {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      try {
        this.processBrokerJobUpdate(data);
        const shipmentId =
          get(data, 'brokerJobs.currentBrokerJobs[0].shipmentId') ||
          this.state.clickedShipId ||
          window.shipmentId;
        if (shipmentId && this.refreshHeaderMessages) {
          this.refreshHeaderMessages(shipmentId);
        }
      } catch (error) {
        console.error('[App] Error in handleEntryStatusUpdate:', error);
      }
    }, 300);
  }

  // Handles broker shipment updates from socket (for single shipment polling)
  handleBrokerShipmentUpdate = (data) => {
    if (this.brokerShipmentDebounceTimer) {
      clearTimeout(this.brokerShipmentDebounceTimer);
    }

    this.brokerShipmentDebounceTimer = setTimeout(() => {
      try {
        this.processBrokerJobUpdate(data);
      } catch (error) {
        console.error('[App] Error in handleBrokerShipmentUpdate:', error);
      }
    }, 300);
  }

  beforeUnloadListener = () => {
    sessionStorage.removeItem("DROPDOWN_VAL")
  }

  // Check if engine shipment (GET_ENTRY_STATUS) should use socket based on country
  // Returns true only if country is in supported list, false otherwise (will use polling)
  // Uses the common utility function from socket/entry-socket/utils
  shouldUseSocketForEngineShipment = () => shouldUseSocketForEngineShipmentUtil()

  /**
   * Re-sends GET_ENTRY_STATUS / SEARCH_SHIPMENTS_BROKER after connect or reconnect.
   * Server registration is per WebSocket session; without this, updates stop after a dropped connection.
   */
  resubscribeShipmentSocketIfNeeded = () => {
    if (window.location.pathname !== "/broker-panel" || !isSocketEnabled()) {
      return;
    }
    if (!socketClient.isConnected() || !this.state.clickedShipId) {
      return;
    }
    const useEntryStatus = getQueryVariable('onlyOneShipment') && getQueryVariable('shipmentHttpId') && getQueryVariable('isShipment') != 'true';
    if (useEntryStatus) {
      if (this.shouldUseSocketForEngineShipment()) {
        this.startEntryStatusPolling(this.state.clickedShipId);
      }
    } else {
      this.startBrokerShipmentPolling(this.state.clickedShipId);
    }
  };

  currentShipmentInterval = (myInterval) => {
    // Clear any existing interval
    if (this.state.currentJobInterval) {
      clearInterval(this.state.currentJobInterval);
    }

    this.resubscribeShipmentSocketIfNeeded();

    // Keep interval for card selection logic
    this.setState({
      currentJobInterval: setInterval(() => {
        let isEngineView = window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN ==  'kxEngineBrokerToken';
        let nonActiveCard = document.getElementsByClassName("broker-card broker-card-wrapper").length;
        let hasActiveCard = document.getElementsByClassName(" broker-card broker-card-wrapper active selected").length;
        if (nonActiveCard && !hasActiveCard && window.shipmentMode !== "LVS_SHIPMENTS" && !(this.state.clickedShipIdHasBeenChanged && isEngineView)) {
          document.getElementsByClassName("broker-card broker-card-wrapper")[0].click();
        }
        // Use polling if socket not connected, or for engine shipments from unsupported countries
        const useEntryStatus = getQueryVariable('onlyOneShipment') && getQueryVariable('shipmentHttpId') && getQueryVariable('isShipment') != 'true';
        if (!isSocketEnabled()||!socketClient.isConnected() || (useEntryStatus && !this.shouldUseSocketForEngineShipment())) {
          this.setCurrentJobData(this.state.clickedShipId);
        }
      }, myInterval)
    });
  }

  // Shared validation for socket polling methods
  validateSocketPolling = (shipmentId, checkConnection = false) => {
    if (!shipmentId) return false;

    const token = getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN);
    if (!token) {
      console.warn('[App] Cannot start listening: token missing');
      return false;
    }

    if (checkConnection && !socketClient.isConnected()) {
      console.warn('[App] Socket not connected, cannot start listening');
      return false;
    }

    return { token, shipmentId };
  }

  // Stops all socket listening
  stopAllSocketListening = () => {
    stopEntryStatusListening();
    stopBrokerShipmentListening();
  }

  // Starts listening to entry status updates using event-based approach
  startEntryStatusPolling = (shipmentId) => {
    const validation = this.validateSocketPolling(shipmentId);
    if (!validation) return;

    // Build extra params for EU countries
    const transactionCountry = getQueryVariable('transactionCountry');
    const extraParams = isEU(transactionCountry) ? {
      transactionType: getQueryVariable('transactionType'),
      countryCode: transactionCountry,
    } : {};

    // Start listening - backend will emit events when status changes
    startEntryStatusListening(
      validation.shipmentId,
      validation.token,
      this.handleEntryStatusUpdate,
      {
        extraParams,
      }
    );
  }

  // Starts listening to broker shipment updates using event-based approach
  startBrokerShipmentPolling = (shipmentId) => {
    const validation = this.validateSocketPolling(shipmentId, true);
    if (!validation) return;

    // Start listening - backend will emit events when shipment data changes
    startBrokerShipmentListening(
      validation.shipmentId,
      validation.token,
      this.handleBrokerShipmentUpdate
    );
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.clickedShipId !== this.state.clickedShipId) {
      // Stop previous listening for both entry status and broker shipment
      this.stopAllSocketListening();
      
      if (this.state.currentJobInterval) {
        clearInterval(this.state.currentJobInterval);
      }
      
      // Start listening for new shipment if socket is connected
      // currentShipmentInterval will determine which handler to use
      this.currentShipmentInterval(6000);
    }
  }

  loadUserSessionData = () => {
    const kxToken = getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN);
    if (!kxToken || window.location.pathname !== "/broker-panel") {
      return;
    }

    this.setUserData();

    API.getPaymentData(kxToken)
      .then((response) => {
        if (response.data) {
          const userPaymentData = response.data.eventMessage.paymentInstruments;
          this.setState({ paymentData: userPaymentData });
        }
      })
      .catch((err) => console.error(err));
  };

  initBrokerPanel = async () => {
    if (this._brokerPanelInitialized) {
      return;
    }
    if (
      !getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN) &&
      window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN == 'kxEngineBrokerToken'
    ) {
      return;
    }

    this._brokerPanelInitialized = true;
    this.loadUserSessionData();

    const queryShipmentId = getQueryVariable("shipmentHttpId");
    if (queryShipmentId) {
      prefetchShipmentDocuments(queryShipmentId).catch(() => {});
    }

    // Initialize WebSocket connection only if socket is enabled for this environment
    if (window.location.pathname === "/broker-panel" && isSocketEnabled()) {
      socketClient.authToken = getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN) || '';
      this.unsubscribeSocketConnection = socketClient.subscribe(
        SYSTEM_EVENTS.CONNECTION_STATE_CHANGE,
        (payload) => {
          if (payload?.state === CONNECTION_STATES.CONNECTED) {
            this.resubscribeShipmentSocketIfNeeded();
          }
        }
      );
      // Don't await - let it connect in background, app continues with HTTP
      socketClient.connect()
        .then(() => {
          console.log('[App] WebSocket connected');
          this.resubscribeShipmentSocketIfNeeded();
        })
        .catch((error) => {
          console.warn('[App] WebSocket connection failed, using HTTP fallback:', error);
        });
    }

    if (window.location.pathname === "/broker-panel") {
      // Document types are not needed to list or open jobs — do not block LCP/jobs on this API.
      this.props.setDocumentTypes();
      if (this.state.loadingJobs) {
        this.getMyBrokerJobs();
      }
      if (this.state.shipId === "NoJobsAvailable") {
        this.getMyBrokerJobs(250);
        this.currentShipmentInterval(6000);
      }
      window.addEventListener("beforeunload", this.beforeUnloadListener, { capture: true });
    }
    if (getQueryVariable("onlyOneShipment")) {
      sessionStorage.setItem("onlyOneShipment", "true");
      jobTransitionAPI.getEntities().then((subCustomersFromEngineParentToBroker) => {
        window.GLOBAL_PARAMETER_OF_SUBCUSTOMERS = subCustomersFromEngineParentToBroker;
      });
    }
  };

  handleEngineTokenReady = () => {
    this.initBrokerPanel();
  };

  async componentDidMount() {
    if (
      !getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN) &&
      window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN == 'kxEngineBrokerToken'
    ) {
      window.addEventListener('engineTokenReady', this.handleEngineTokenReady);
      return;
    }

    await this.initBrokerPanel();
  }

  componentWillUnmount() {
    window.removeEventListener('engineTokenReady', this.handleEngineTokenReady);

    // Clear debounce timers on unmount
    this.clearAllDebounceTimers();
    
    // Cleanup all socket listening
    this.stopAllSocketListening();

    if (this.unsubscribeSocketConnection) {
      this.unsubscribeSocketConnection();
      this.unsubscribeSocketConnection = null;
    }

    // Cleanup socket connection and listeners
    socketClient.cleanup();
    
    // Clear intervals
    if (this.state.currentJobInterval) {
      clearInterval(this.state.currentJobInterval);
    }
    //clearInterval(this.state.jobInterval)
    
    // Clean up window properties
    if (this.state.shipId && this.state.shipId !== "NoJobsAvailable") {
      delete window[`GLOBAL_LastJobData${this.state.shipId}`];
    }
    if (this.state.clickedShipId) {
      delete window[`GLOBAL_LastJobData${this.state.clickedShipId}`];
    }
    window.removeEventListener("beforeunload", this.beforeUnloadListener, { capture: true });
  }

  getMyBrokerJobs = async () => {
    this.props.getBrokerShipmentList(()=>{},(resAgain)=>{
        let newShipmentId = get(resAgain,"data.eventMessage.brokerJobs.currentBrokerJobs[0].shipmentId");
        if (newShipmentId) {
          prefetchShipmentDocuments(newShipmentId).catch(() => {});
        }
        if(!this.state.autosetClcikedShipmentId && newShipmentId){
          this.setState({autosetClcikedShipmentId:true});
          this.getClickedShipId(newShipmentId);
          try {
            document.getElementsByClassName("broker-card broker-card-wrapper")[0].click();
          } catch(eeee){}
        }
      })
      .then((res) => {
        this.setState({
          loadingJobs: false,
          newerDisabled: false
        });
      });
  };

  static routeUser() {
    document.location = "/broker-panel"+window.location.search;
  }

  toggleState = (target = "") => {
    target !== "" && this.setState({ [target]: !this.state[target] });
  };

  getClickedShipId = (id) => {
    this.setState({ clickedShipId: id, clickedShipIdHasBeenChanged:true },
      ()=>{this.setCurrentJobData(id)}
      );
  };

  toggle = (name, value) => this.setState({ [name]: value });

  // setShipmentMode= (mode) => {
  //   this.setState({"shipmentMode":mode})
  // }
  render() {
    // const {contextState , toggleModalfromContext ,toggleAccountMenu,myAccountOpen} = useAppContext()
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      kxToken: getCookie(window.GLOBAL_MAIN_TOKEN_KXBROKERTOKEN)
    };
    let kxUser = sessionStorage.getItem("kxUser");
    let filerCode = get(kxUser, "userInfo.brokerAdditionalInfo.accountSecurityNumber") || get(kxUser, "userInfo.brokerAdditionalInfo.filerCode")
    return (
      <div className={(getQueryVariable("onlyOneShipment") ||sessionStorage.getItem("onlyOneShipment")?'onlyShowOneShipment':'')+
          (getQueryVariable('docOnlyBroker')=='true'?' docOnlyBroker ':' ')}>
      <ToastProvider>
        <AppProvider>
          <AppContext.Consumer>
            {(context) => (
              <Router>
              <React.Fragment>
                <Switch>
                  <Route exact path="/" render={(props) => (
                    isEngineIframeLoad()
                      ? <Redirect to={getEngineIframeTargetPath()} />
                      : <AsyncLogin {...props} mainState={this.state} shipId={this.state.shipId} routeUser={App.routeUser} />
                  )} />
                  <Route exact path="/forgot" render={(props) => <AsyncForgot {...props} mainState={this.state} routeUser={App.routeUser} />} />
                  <Route exact path="/reset-password/:tmp" render={(props) => <AsyncReset {...props} mainState={this.state} routeUser={App.routeUser} />} />
                  <Route exact path="/custom-statement"
                    render={() => (
                      <Suspense fallback={'Loading...'}>
                        <PreDailyPageLazy
                          infoFilled={this.state.infoFilled}
                          kxUser={this.state.kxUser || JSON.parse(sessionStorage.getItem("kxUser"))}
                          getPaymentData={this.getPaymentData}
                          setUserData={this.setUserData}
                        />
                      </Suspense>
                    )}
                  />
                  <Route exact path="/invoices-us"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <InvoiceWrapper country={'us'}><InvoiceUs /></InvoiceWrapper>
                      </Suspense>
                    )}
                  />
                  <Route exact path="/invoices-ca"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <InvoiceWrapper country={'ca'}><InvoiceCanada /></InvoiceWrapper>
                      </Suspense>
                    )}
                  />
                  <Route exact path="/parts-library"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <ManageProductHistory
                          filerCode={filerCode}
                        />
                      </Suspense>
                    )}
                  />
                  <Route exact path="/parts"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <Parts
                          filerCode={filerCode}
                        />
                      </Suspense>
                    )}
                  />
                  <Route
                    exact
                    path="/broker-panel"
                    render={() => (
                      <>
                        {!(getQueryVariable("onlyOneShipment") || sessionStorage.getItem("onlyOneShipment")) && (
                        <JobQueueHeader
                          toggle={context.toggleAccountMenu}
                          toggleModal={context.toggleModalfromContext}
                          myAccountOpen={context.contextState.myAccountOpen}
                          profile={this.state.kxUser}
                        />
                        )}
                          <AsyncJobQueue
                          currentJobState={this.props.currentJobState}
                          docTypes={this.props.docTypes}
                          currentBrokerJobs={this.props.brokerJobs}
                          getMyBrokerJobs={this.getMyBrokerJobs}
                          infoFilled={this.state.infoFilled}
                          toggleModal={context.toggleModalfromContext}
                          toggle={context.toggleAccountMenu}
                          filteredJobs={this.state.filteredJobs}
                          toggleState={this.toggleState}
                          headers={headers}
                          getClickedShipId={this.getClickedShipId}
                          mainState={this.state}
                          customModalOpen={context.contextState.modalOpen}
                          customAccountMenuOpen={context.contextState.myAccountOpen}
                          shipId={this.state.shipId}
                          getCustomerShipments={this.getCustomerShipments}
                          kxUser={this.state.kxUser}
                          paymentData={this.state.paymentData}
                          setUserData={this.setUserData}
                          getPaymentData={this.getPaymentData}
                          shipmentCountry={this.state.shipmentCountry}
                          setShipmentCountry={this.setShipmentCountry}
                          registerHeaderMessagesRefresh={this.registerHeaderMessagesRefresh}
                        />
                       
                      </>
                    )}
                  />
                  <Route
                    exact
                    path="/broker-panel/xml-preview/:countryCode/:shipId/:status/:responseId"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <XmlRendererWithRouter />
                      </Suspense>
                    )}
                  />
                  <Route
                    exact
                    path="/broker-panel/xml-review/:shipId/:formType"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <XmlReviewerWithRouter />
                      </Suspense>
                    )}
                  />
                  <Route
                    exact
                    path="/broker-panel/xml-sent/:countryCode/:shipId"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <XmlSentWithRouter />
                      </Suspense>
                    )}
                  />
                  <Route
                    exact
                    path="/broker-panel/xml-request/:countryCode/:shipId/:requestId"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <XmlRequestWithRouter />
                      </Suspense>
                    )}
                  />
                  <Route
                    exact
                    path="/broker-panel/recon"
                    render={() => (
                      <Suspense fallback={<RouteFallback />}>
                        <ReconApp kxUser={this.state.kxUser} />
                      </Suspense>
                    )}
                  />
                    {showChatApp() && (
                    <Route
                      exact
                      path="/chat/:conversationId?"
                      render={({ match, history }) => (
                        <Suspense fallback={<RouteFallback />}>
                          <ConversationsContainer
                            match={match}
                            history={history}
                          />
                        </Suspense>
                      )}
                    />
                    )}

                </Switch>
              </React.Fragment>
            </Router>
          )}
        </AppContext.Consumer>
      </AppProvider>
      </ToastProvider>
      </div>
    );
  }
}

export default WithChatService(App);